/**
 * Imperial Yeast (Portland) — catalogued in full from imperialyeast.com,
 * including the seasonal releases and the lower-case `i` Innovation series.
 *
 * Temperatures are °C as Imperial publishes them alongside °F (PLAN §5 canon),
 * and attenuation is apparent attenuation. `alcoholTolerancePercent` is
 * Imperial's single published % figure.
 *
 * Imperial prints a Diastatic result on every strain page, so `sta1` is a
 * published test result rather than a guess; it is `null` only for the few
 * cultures where they leave the field blank.
 *
 * W25 Lacto Brevis is listed at 0 % attenuation, which is a placeholder rather
 * than a measurement, so that entry alone carries a class-typical figure and
 * is marked `representative`.
 */

import type { Yeast } from '../../schema/yeast';

/** Imperial Yeast cultures. @see Yeast */
export const imperialYeasts: readonly Yeast[] = [
  {
    id: 'imperial-a01',
    lab: 'Imperial Yeast',
    productCode: 'A01',
    name: 'House',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 75 },
    temperatureC: { min: 17, max: 21 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'An English house strain that lets both malt and hops show: moderate fruit, a soft finish and a hard floc. The default Imperial pick for bitters, pale ales and anything that should taste balanced rather than of the yeast.',
    tags: ['english', 'house', 'bitter', 'pale-ale', 'balanced', 'clearing'],
    url: 'https://www.imperialyeast.com/yeast-strains/house',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-a04',
    lab: 'Imperial Yeast',
    productCode: 'A04',
    name: 'Barbarian',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 74 },
    temperatureC: { min: 17, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A hop-forward English-leaning strain that made its name in juicy IPA: stone fruit esters, a rounded body and enough attenuation to keep a haze from tasting sweet. Also a capable house yeast if you ferment it cool.',
    tags: ['hazy', 'ipa', 'english', 'stone-fruit', 'hop-forward'],
    url: 'https://www.imperialyeast.com/yeast-strains/barbarian',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-a05',
    lab: 'Imperial Yeast',
    productCode: 'A05',
    name: 'Voyager',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 68, max: 72 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'Biscuit and peach over a smooth, malt-forward body, with good flocculation. It under-attenuates a little, which is what leaves the biscuit character in place.',
    tags: ['biscuit', 'peach', 'smooth', 'english', 'flocculent', 'malty'],
    url: 'https://www.imperialyeast.com/yeast-strains/voyager',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-a07',
    lab: 'Imperial Yeast',
    productCode: 'A07',
    name: 'Flagship',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 16, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      "Imperial's clean American house strain and the default for anything hop-forward. Neutral fermentation character, dependable attenuation and enough flocculation to package without a long cold rest.",
    tags: ['clean', 'neutral', 'american', 'pale-ale', 'ipa', 'house'],
    url: 'https://www.imperialyeast.com/yeast-strains/flagship',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-a09',
    lab: 'Imperial Yeast',
    productCode: 'A09',
    name: 'Pub',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 69, max: 74 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A London-style ale strain that leaves a touch of residual sweetness and a light pear ester, which is exactly what a cask bitter or brown ale wants. Drops clear quickly, so it is easy to rack and condition.',
    tags: ['english', 'bitter', 'mild', 'malty', 'esters', 'cask'],
    url: 'https://www.imperialyeast.com/yeast-strains/pub',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-a10',
    lab: 'Imperial Yeast',
    productCode: 'A10',
    name: 'Darkness',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 71, max: 75 },
    temperatureC: { min: 17, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'An Irish-descended strain aimed at stouts and porters: it finishes with body and a faint minerality that keeps roast from tasting hollow. Gives a slight diacetyl push early on, so leave it warm for a couple of days before crashing.',
    tags: ['stout', 'porter', 'irish', 'roast', 'malty'],
    url: 'https://www.imperialyeast.com/yeast-strains/darkness',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-a15',
    lab: 'Imperial Yeast',
    productCode: 'A15',
    name: 'Independence',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 16, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'An American ale with a little more ester than Flagship and a clear affection for hops. Use it when a pale ale or IPA should taste clean but not anonymous.',
    tags: ['american', 'ipa', 'pale-ale', 'esters', 'hop-forward'],
    url: 'https://www.imperialyeast.com/yeast-strains/independence',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-a18',
    lab: 'Imperial Yeast',
    productCode: 'A18',
    name: 'Joystick',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 77 },
    temperatureC: { min: 16, max: 21 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A robust, balanced Northwest pale ale strain with good flocculation. It ferments reliably at the cool end where other American strains slow down.',
    tags: ['american', 'pacific-northwest', 'balanced', 'robust', 'pale-ale', 'flocculent'],
    url: 'https://www.imperialyeast.com/yeast-strains/joystick',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-a20',
    lab: 'Imperial Yeast',
    productCode: 'A20',
    name: 'Citrus',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 75 },
    temperatureC: { min: 19, max: 27 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: true,
    description:
      'The New England strain that made hazy IPA possible: stone fruit and citrus esters, moderate attenuation and a soft mouthfeel it holds onto even at 8% ABV. Slow to settle, which is part of the point.',
    tags: ['hazy', 'new-england', 'ipa', 'stone-fruit', 'citrus', 'esters'],
    url: 'https://www.imperialyeast.com/yeast-strains/citrus',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-a24',
    lab: 'Imperial Yeast',
    productCode: 'A24',
    name: 'Dry Hop',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 75 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: true,
    description:
      'A two-strain blend picked for hop-forward beers: one partner biotransforms, the other finishes clean. Expect a rounded, slightly sweet middle and a haze that hangs around after the dry hop.',
    tags: ['hazy', 'ipa', 'blend', 'biotransformation', 'hop-forward'],
    url: 'https://www.imperialyeast.com/yeast-strains/dry-hop',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-a30',
    lab: 'Imperial Yeast',
    productCode: 'A30',
    name: 'Corporate',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 16, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A balanced American strain that suits hop-forward beers across a wide temperature band. Neither as neutral as Flagship nor as expressive as the hazy strains.',
    tags: ['american', 'balanced', 'hoppy', 'versatile', 'pale-ale', 'wide-range'],
    url: 'https://www.imperialyeast.com/yeast-strains/corporate',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-a31',
    lab: 'Imperial Yeast',
    productCode: 'A31',
    name: 'Tartan',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A Scottish strain for malty, robust beers, at home in a wee heavy or an 80 shilling. It leaves body behind rather than drying the malt out.',
    tags: ['scottish', 'malty', 'robust', 'strong-ale', 'wee-heavy'],
    url: 'https://www.imperialyeast.com/yeast-strains/tartan',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-a34',
    lab: 'Imperial Yeast',
    productCode: 'A34',
    name: 'Julius',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 18, max: 20 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'Apricot and zest over a hop-forward beer, and unusually flocculent for a fruity strain. It likes a narrow temperature band, 18–20 °C, and rewards holding it there.',
    tags: ['apricot', 'hoppy', 'zesty', 'flocculent', 'ipa', 'hazy'],
    url: 'https://www.imperialyeast.com/yeast-strains/julius',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-a37',
    lab: 'Imperial Yeast',
    productCode: 'A37',
    name: 'POG',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 75, max: 85 },
    temperatureC: { min: 29, max: 38 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'The Ebbegarden kveik, giving tropical fruit at 29–38 °C and flocculating hard once it is done. A fast beer that still tastes like it took its time.',
    tags: ['kveik', 'ebbegarden', 'tropical', 'hot', 'fast', 'flocculent'],
    url: 'https://www.imperialyeast.com/yeast-strains/pog',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-a38',
    lab: 'Imperial Yeast',
    productCode: 'A38',
    name: 'Juice',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A hazy-IPA strain chosen for biotransformation rather than its own esters — it works with a dry hop instead of shouting over it. Expect a full, slightly sweet finish and persistent haze.',
    tags: ['hazy', 'ipa', 'biotransformation', 'new-england', 'tropical'],
    url: 'https://www.imperialyeast.com/yeast-strains/juice',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-a40',
    lab: 'Imperial Yeast',
    productCode: 'A40',
    name: 'Bubbles',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 85, max: 100 },
    temperatureC: { min: 14, max: 25 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A cider strain that attenuates to bone dryness across a wide temperature range. It is phenol-positive, which suits apple better than it would suit beer.',
    tags: ['cider', 'crisp', 'dry', 'attenuative', 'phenolic', 'wide-range'],
    url: 'https://www.imperialyeast.com/yeast-strains/bubbles',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-a43',
    lab: 'Imperial Yeast',
    productCode: 'A43',
    name: 'Loki',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 75, max: 85 },
    temperatureC: { min: 18, max: 38 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      "Imperial's Voss kveik: nearly silent at the cool end, a blast of citrus ester at 30–35 °C, and a two-or-three-day finish either way. Versatile enough for pseudo-lagers, farmhouse ales and hop-forward beers.",
    tags: ['kveik', 'voss', 'citrus', 'norwegian', 'fast', 'versatile'],
    url: 'https://www.imperialyeast.com/yeast-strains/loki',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-a44',
    lab: 'Imperial Yeast',
    productCode: 'A44',
    name: 'Kveiking',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 75, max: 85 },
    temperatureC: { min: 24, max: 36 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A three-kveik blend that throws pineapple, guava and tropical fruit when fermented hot. Keep the temperature up or attenuation drops; clarity is a coin toss because the blend mixes flocculent and powdery strains.',
    tags: ['kveik', 'blend', 'tropical', 'pineapple', 'guava', 'fast', 'ipa'],
    url: 'https://www.imperialyeast.com/yeast-strains/kveiking',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-a46',
    lab: 'Imperial Yeast',
    productCode: 'A46',
    name: 'Bartleby',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 75, max: 85 },
    temperatureC: { min: 20, max: 37 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'Hornindal kveik as Imperial isolates it: peach, pineapple and citrus with a hard floc. Ferment it hot under tropical hops for a hazy IPA, or cooler if you want the fruit to sit behind the malt.',
    tags: ['kveik', 'hornindal', 'tropical', 'peach', 'norwegian', 'fast'],
    url: 'https://www.imperialyeast.com/yeast-strains/bartleby',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-b44',
    lab: 'Imperial Yeast',
    productCode: 'B44',
    name: 'Whiteout',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 17, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A Belgian witbier strain giving clove-leaning phenols and a light citrus ester that sits under coriander and orange peel rather than fighting them. Low flocculation keeps the beer attractively cloudy.',
    tags: ['witbier', 'belgian', 'phenolic', 'wheat', 'spicy'],
    url: 'https://www.imperialyeast.com/yeast-strains/whiteout',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-b45',
    lab: 'Imperial Yeast',
    productCode: 'B45',
    name: 'Gnome',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 78 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A bright, flocculent Belgian strain that works across the range rather than specializing. Unusual in dropping clear without a long wait.',
    tags: ['belgian', 'bright', 'flocculent', 'versatile', 'phenolic', 'pale-ale'],
    url: 'https://www.imperialyeast.com/yeast-strains/gnome',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-b48',
    lab: 'Imperial Yeast',
    productCode: 'B48',
    name: 'Triple Double',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 78 },
    temperatureC: { min: 18, max: 25 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A classic high-gravity Belgian abbey strain for dubbel, tripel and quadrupel: dark fruit, restrained clove and the alcohol tolerance to actually finish. Start cool and let it rise.',
    tags: ['belgian', 'abbey', 'tripel', 'dubbel', 'quadrupel', 'phenolic'],
    url: 'https://www.imperialyeast.com/yeast-strains/triple-double',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-b51',
    lab: 'Imperial Yeast',
    productCode: 'B51',
    name: 'Workhorse',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 77 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'An aggressive Ellezelles strain that ferments hard and stays low in phenol, which is unusual for a saison yeast. Fruit rather than spice.',
    tags: ['belgian', 'saison', 'ellezelles', 'aggressive', 'low-phenol', 'attenuative'],
    url: 'https://www.imperialyeast.com/yeast-strains/workhorse',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-b53',
    lab: 'Imperial Yeast',
    productCode: 'B53',
    name: 'Precious',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 78 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A Gaume farmhouse strain intended for primary fermentation rather than as a blending partner. Restrained and noble where the Dupont strains are rustic.',
    tags: ['belgian', 'gaume', 'saison', 'noble', 'primary', 'farmhouse'],
    url: 'https://www.imperialyeast.com/yeast-strains/precious',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-b56',
    lab: 'Imperial Yeast',
    productCode: 'B56',
    name: 'Rustic',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 20, max: 27 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: true,
    description:
      'A classic Wallonian saison strain: peppery phenols, a bone-dry finish and a well-earned reputation for stalling around 1.030 if it gets cold. Start warm and let the temperature free-rise rather than holding it down.',
    tags: ['saison', 'belgian', 'farmhouse', 'phenolic', 'dry-finish'],
    url: 'https://www.imperialyeast.com/yeast-strains/rustic',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-b63',
    lab: 'Imperial Yeast',
    productCode: 'B63',
    name: 'Monastic',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 78 },
    temperatureC: { min: 20, max: 26 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'The Chimay-style strain: aromatic, phenol-forward and built for strong Belgian ales. It wants a warm finish and plenty of headspace.',
    tags: ['belgian', 'chimay', 'aromatic', 'strong', 'trappist', 'phenolic'],
    url: 'https://www.imperialyeast.com/yeast-strains/monastic',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-b64',
    lab: 'Imperial Yeast',
    productCode: 'B64',
    name: 'Napoleon',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 77, max: 83 },
    temperatureC: { min: 18, max: 26 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: true,
    description:
      'A French saison strain balancing fruit esters against peppery phenols, and dry enough to finish a farmhouse ale without the Dupont stall. Diastatic — confirm gravity before packaging.',
    tags: ['saison', 'french', 'farmhouse', 'phenolic', 'diastatic', 'dry-finish'],
    url: 'https://www.imperialyeast.com/yeast-strains/napoleon',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-b66',
    lab: 'Imperial Yeast',
    productCode: 'B66',
    name: 'Fillibuster',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 20, max: 32 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: true,
    description:
      'The Dupont saison strain, expressive and demanding: it wants real heat and will stall if it does not get it. It carries STA1 and finishes very dry once it does.',
    tags: ['saison', 'dupont', 'expressive', 'sta1', 'hot', 'stalls', 'low-floc'],
    url: 'https://www.imperialyeast.com/yeast-strains/fillibuster',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-b75',
    lab: 'Imperial Yeast',
    productCode: 'B75',
    name: 'Priory',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 78 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'An abbey strain that stays mild for its family, with the alcohol tolerance for a strong beer and better flocculation than most. A good starting point for a first Belgian.',
    tags: ['belgian', 'abbey', 'mild', 'high-gravity', 'flocculent', 'balanced'],
    url: 'https://www.imperialyeast.com/yeast-strains/priory',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-g01',
    lab: 'Imperial Yeast',
    productCode: 'G01',
    name: 'Stefon',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 17, max: 23 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A traditional hefeweizen strain: banana against clove, the split shifting with pitch rate and temperature. It stays in the glass, which is the point of a weizen and a problem for anything you need bright.',
    tags: ['hefeweizen', 'wheat', 'banana', 'clove', 'phenolic', 'german'],
    url: 'https://www.imperialyeast.com/yeast-strains/stefon',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-g02',
    lab: 'Imperial Yeast',
    productCode: 'G02',
    name: 'Kaiser',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 13, max: 18 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A German alt strain that ferments cold, 13–18 °C, and stays clean and malty throughout. Versatile enough to double as a cold-fermented ale yeast.',
    tags: ['alt', 'german', 'versatile', 'clean', 'cold', 'malty'],
    url: 'https://www.imperialyeast.com/yeast-strains/kaiser',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-g03',
    lab: 'Imperial Yeast',
    productCode: 'G03',
    name: 'Dieter',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 16, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A traditional kölsch strain: clean, faintly fruity and happy to drop with a cold rest. Ferment at the bottom of the range and lager it if you want the beer to taste like Cologne rather than like an ale.',
    tags: ['kolsch', 'hybrid', 'clean', 'german', 'crisp'],
    url: 'https://www.imperialyeast.com/yeast-strains/dieter',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-g05',
    lab: 'Imperial Yeast',
    productCode: 'G05',
    name: 'Kurt',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 13, max: 18 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      "A balanced, clean alt strain for cold ale fermentation. Seasonal in Imperial's calendar, so plan the brew day around its release.",
    tags: ['alt', 'german', 'balanced', 'clean', 'cold', 'seasonal'],
    url: 'https://www.imperialyeast.com/yeast-strains/kurt',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-g17',
    lab: 'Imperial Yeast',
    productCode: 'G17',
    name: 'Gustav',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 79 },
    temperatureC: { min: 14, max: 20 },
    flocculation: 'low',
    alcoholTolerancePercent: 8,
    sta1: false,
    description:
      'An aromatic German strain with a Gewürztraminer character and a powdery mouthfeel. Its alcohol tolerance is only 8%, so keep the gravity modest.',
    tags: ['gewurztraminer', 'powdery', 'refreshing', 'aromatic', 'low-alcohol', 'german'],
    url: 'https://www.imperialyeast.com/yeast-strains/gustav',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-l02',
    lab: 'Imperial Yeast',
    productCode: 'L02',
    name: 'Fest',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 12, max: 14 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A malty, flocculent lager strain for märzen and Oktoberfest, happiest at 12–14 °C. It drops well, which shortens the lagering.',
    tags: ['lager', 'marzen', 'oktoberfest', 'malty', 'flocculent', 'cold'],
    url: 'https://www.imperialyeast.com/yeast-strains/fest',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-l05',
    lab: 'Imperial Yeast',
    productCode: 'L05',
    name: 'Cablecar',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 71, max: 75 },
    temperatureC: { min: 13, max: 18 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'The California common steam-beer strain: lager-clean at ale-cool temperatures, with the woody, faintly minty note the style is built on. Best around 16–18 °C, where it stays clean without needing a cold room.',
    tags: ['hybrid', 'california-common', 'steam-beer', 'lager', 'crisp'],
    url: 'https://www.imperialyeast.com/yeast-strains/cablecar',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-l09',
    lab: 'Imperial Yeast',
    productCode: 'L09',
    name: 'Que Bueno',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 8, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A Mexican lager strain, balanced and dry, and clean at proper lager temperatures. A good base for a pale adjunct lager.',
    tags: ['lager', 'mexican', 'balanced', 'dry', 'clean', 'cold'],
    url: 'https://www.imperialyeast.com/yeast-strains/que-bueno',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-l11',
    lab: 'Imperial Yeast',
    productCode: 'L11',
    name: 'Gateway',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 8, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: false,
    description:
      'A clean, crisp lager strain that flocculates well and keeps sulfur low. Straightforward, which is what most pilsners want.',
    tags: ['lager', 'clean', 'crisp', 'flocculent', 'cold', 'pilsner'],
    url: 'https://www.imperialyeast.com/yeast-strains/gateway',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-l13',
    lab: 'Imperial Yeast',
    productCode: 'L13',
    name: 'Global',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 8, max: 13 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'The Weihenstephan 34/70-type lager strain in liquid form — the most widely used lager yeast in the world, and neutral enough to suit anything from helles to schwarzbier. Tolerates the warm end of lager temperatures without throwing sulfur it cannot clean up.',
    tags: ['lager', 'clean', 'german', 'helles', 'pilsner', 'neutral'],
    url: 'https://www.imperialyeast.com/yeast-strains/global',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-l17',
    lab: 'Imperial Yeast',
    productCode: 'L17',
    name: 'Harvest',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 74 },
    temperatureC: { min: 10, max: 16 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A classic lager strain notable for low sulfur production, which shortens the conditioning you would otherwise owe it. Versatile across the German styles.',
    tags: ['lager', 'classic', 'low-sulfur', 'versatile', 'malty'],
    url: 'https://www.imperialyeast.com/yeast-strains/harvest',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-l18',
    lab: 'Imperial Yeast',
    productCode: 'L18',
    name: 'The GOAT',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 74 },
    temperatureC: { min: 9, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A Bavarian strain, balanced and smooth, with the alcohol tolerance for a bock. It under-attenuates a little, leaving malt where you put it.',
    tags: ['lager', 'bavarian', 'balanced', 'smooth', 'malty', 'high-gravity'],
    url: 'https://www.imperialyeast.com/yeast-strains/the-goat',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-l25',
    lab: 'Imperial Yeast',
    productCode: 'L25',
    name: 'Hygge',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 8, max: 13 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A Copenhagen strain that keeps malt secondary and lets the hops through, finishing crisp. Useful for hoppy pilsners where a malty lager yeast would fight the recipe.',
    tags: ['lager', 'copenhagen', 'crisp', 'hop-friendly', 'clean', 'low-floc'],
    url: 'https://www.imperialyeast.com/yeast-strains/hygge',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-l26',
    lab: 'Imperial Yeast',
    productCode: 'L26',
    name: 'Pilgrimage',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 77 },
    temperatureC: { min: 7, max: 13 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'An elegant Bavarian monastery strain that flocculates well and takes a strong lager without flagging. It ferments down to 7 °C, colder than most.',
    tags: ['lager', 'bavarian', 'monastery', 'elegant', 'high-gravity', 'flocculent'],
    url: 'https://www.imperialyeast.com/yeast-strains/pilgrimage',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-l28',
    lab: 'Imperial Yeast',
    productCode: 'L28',
    name: 'Urkel',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 71, max: 75 },
    temperatureC: { min: 11, max: 14 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A Czech lager strain for pilsner that should taste of malt and Saaz, not of the yeast. Fuller than a German 34/70 type; give it a long cold rest so the sulfur has somewhere to go.',
    tags: ['lager', 'czech', 'pilsner', 'malty', 'traditional'],
    url: 'https://www.imperialyeast.com/yeast-strains/urkel',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-l29',
    lab: 'Imperial Yeast',
    productCode: 'L29',
    name: 'Lookr',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 10, max: 14 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A clean, balanced lager strain that flocculates hard, which makes for short tank time. A practical choice when the schedule matters more than the nuance.',
    tags: ['lager', 'balanced', 'clean', 'flocculent', 'versatile'],
    url: 'https://www.imperialyeast.com/yeast-strains/lookr',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-l32',
    lab: 'Imperial Yeast',
    productCode: 'L32',
    name: 'Magnitude',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 74 },
    temperatureC: { min: 9, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A malty lager strain with the tolerance for a doppelbock, under-attenuating to keep body in a big beer. Balanced rather than assertive.',
    tags: ['lager', 'balanced', 'high-gravity', 'malty', 'bock', 'doppelbock'],
    url: 'https://www.imperialyeast.com/yeast-strains/magnitude',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-l36',
    lab: 'Imperial Yeast',
    productCode: 'L36',
    name: 'Ray Gun',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 10, max: 14 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A smooth, clean, balanced lager strain with nothing to trip over. A reasonable house lager yeast when no style is calling for something specific.',
    tags: ['lager', 'balanced', 'clean', 'smooth', 'versatile'],
    url: 'https://www.imperialyeast.com/yeast-strains/ray-gun',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-l48',
    lab: 'Imperial Yeast',
    productCode: 'L48',
    name: 'Alpha',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 8, max: 13 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'Built for hop-forward lagers: a clean, crisp profile with balanced sulfur production, aimed at cold IPA and West Coast pilsner. It ferments down to 8 °C.',
    tags: ['lager', 'crisp', 'hop-friendly', 'west-coast-pilsner', 'cold-ipa', 'clean'],
    url: 'https://www.imperialyeast.com/yeast-strains/alpha',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-l51',
    lab: 'Imperial Yeast',
    productCode: 'L51',
    name: 'Never',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 74 },
    temperatureC: { min: 9, max: 13 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 9,
    sta1: false,
    description:
      'A clean, crisp lager strain that flocculates well and keeps out of the way. Its alcohol tolerance is only 9%, so keep it to standard-gravity lagers.',
    tags: ['lager', 'balanced', 'clean', 'crisp', 'flocculent'],
    url: 'https://www.imperialyeast.com/yeast-strains/never',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-l55',
    lab: 'Imperial Yeast',
    productCode: 'L55',
    name: 'Kanpai',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 12, max: 14 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A very flocculent lager strain with a smooth finish, at home in rice and adjunct lagers. It drops so hard that the beer is bright almost as soon as it is cold.',
    tags: ['lager', 'flocculent', 'smooth', 'japanese', 'rice-lager', 'clean'],
    url: 'https://www.imperialyeast.com/yeast-strains/kanpai',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-l76',
    lab: 'Imperial Yeast',
    productCode: 'L76',
    name: 'Clyde',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 8, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: false,
    description:
      'An American lager strain, clean and balanced, for pale adjunct lagers. Modest alcohol tolerance at 9%, which is all the style asks for.',
    tags: ['lager', 'american', 'balanced', 'clean', 'adjunct', 'cold'],
    url: 'https://www.imperialyeast.com/yeast-strains/clyde',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-w01',
    lab: 'Imperial Yeast',
    productCode: 'W01',
    name: 'Champion',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 65, max: 100 },
    temperatureC: { min: 13, max: 23 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A champagne strain for seltzer, cider and anything else that should finish at 1.000. Neutral by design, so the fruit or the flavoring carries the drink.',
    tags: ['champagne', 'neutral', 'seltzer', 'cider', 'attenuative', 'high-gravity'],
    url: 'https://www.imperialyeast.com/yeast-strains/champion',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-w04',
    lab: 'Imperial Yeast',
    productCode: 'W04',
    name: 'Paramount',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 90, max: 100 },
    temperatureC: { min: 18, max: 32 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: null,
    description:
      'A seltzer strain that attenuates to 100% and tolerates 14% alcohol, running warm the whole way. Nothing of itself in the glass, which is the specification.',
    tags: ['seltzer', 'clean', 'attenuative', 'high-gravity', 'neutral', 'warm'],
    url: 'https://www.imperialyeast.com/yeast-strains/paramount',
    provenance: 'spec-sheet',
  },
  // W15 and W25 are no longer on Imperial's strain list and their pages 404 as
  // of October 2026, so they carry no link. The rows stay so recipes and
  // inventory that use them still resolve.
  {
    id: 'imperial-w15',
    lab: 'Imperial Yeast',
    productCode: 'W15',
    name: 'Suburban Brett',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      "Imperial's house Brettanomyces: pie cherry and tropical fruit rather than barnyard. Happier as a secondary or co-pitch than as a three-day primary; attenuation here is class-typical of a Brett given time.",
    tags: ['brett', 'wild', 'cherry', 'tropical', 'secondary', 'mixed-fermentation'],
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-w25',
    lab: 'Imperial Yeast',
    productCode: 'W25',
    name: 'Lacto Brevis',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 30, max: 40 },
    temperatureC: { min: 29, max: 38 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Lactobacillus brevis for a quick kettle sour: lactic acid in a day or two when held warm, and hop-sensitive enough that you should sour before you bitter. It does not ferment alcohol — pitch a Saccharomyces afterwards. Attenuation is nominal.',
    tags: ['sour', 'lactobacillus', 'kettle-sour', 'brevis', 'gose', 'berliner-weisse'],
    provenance: 'representative',
  },
  {
    id: 'imperial-i10',
    lab: 'Imperial Yeast',
    productCode: 'i10',
    name: 'Mangosteeni',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 26, max: 32 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'An Innovation-series strain giving lychee, strawberry and ripe fruit at 26–32 °C. Hot and fast, with the aroma of a kveik and a rounder body.',
    tags: ['lychee', 'strawberry', 'ripe-fruit', 'hot', 'fast', 'hazy', 'innovation'],
    url: 'https://www.imperialyeast.com/yeast-strains/mangosteeni',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-i22',
    lab: 'Imperial Yeast',
    productCode: 'i22',
    name: 'Capri',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 68, max: 74 },
    temperatureC: { min: 20, max: 30 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'An Innovation-series hybrid built for juicy, tropical character at 20–30 °C. Warmer than an ale strain, cooler than a kveik, and it splits the difference in flavor too.',
    tags: ['hybrid', 'juicy', 'tropical', 'warm', 'ipa', 'innovation'],
    url: 'https://www.imperialyeast.com/yeast-strains/capri',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-i32',
    lab: 'Imperial Yeast',
    productCode: 'i32',
    name: 'Magic J',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'An Innovation-series strain for hazy, juicy IPA, fruity and slow to drop. Close to Juice in intent, with more fruit and less flocculation.',
    tags: ['fruity', 'hazy', 'juicy', 'neipa', 'ipa', 'innovation'],
    url: 'https://www.imperialyeast.com/yeast-strains/magic-j',
    provenance: 'spec-sheet',
  },
  {
    id: 'imperial-a62',
    lab: 'Imperial Yeast',
    productCode: 'A62',
    name: "Bell's House",
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The Bell’s Brewery house strain, isolated in their own lab and packaged by Imperial: the yeast behind Two Hearted, Oberon and Hopslam, and behind most of their ales that are not Belgian. An American strain with esters it will show you if you let it warm, and hide if you keep it cool. Imperial do not list it among their public strains — it is sold through Bell’s — so the figures here are Bell’s own published ones and there is no maker page to link.',
    tags: ['american', 'house-strain', 'bells', 'two-hearted', 'ipa', 'estery'],
    provenance: 'spec-sheet',
  },
];
